import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import NotificationBadge from './NotificationBadge';
import { getImageUrl } from '../utils/imageUrl';
import { useTheme } from '../utils/theme';
import './Sidebar.css';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || ''}/api`;

const Sidebar = ({ activeTab, onTabChange, sidebarOpen = false }) => {
  const { t } = useTranslation('navigation');
  const { t: tApi } = useTranslation('apiIntegration');
  const { isDark, toggleTheme } = useTheme();
  const [profileImage, setProfileImage] = useState(null);
  const [logoUrl, setLogoUrl] = useState('/logo192.png');
  
  // Get admin user info from localStorage
  const adminUser = JSON.parse(localStorage.getItem('user') || '{}');
  
  // Get user role (lowercase) for role-based filtering
  const userRole = (adminUser.role || 'admin').toLowerCase();
  
  // UX Best Practice: Display full name (first_name + last_name)
  // Properly capitalize: First letter uppercase, rest lowercase
  const capitalizeName = (name) => {
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };
  
  const getAdminDisplayName = () => {
    if (adminUser.first_name && adminUser.last_name) {
      // Display full name: "Soufiane Rihani"
      return `${capitalizeName(adminUser.first_name)} ${capitalizeName(adminUser.last_name)}`.trim();
    }
    if (adminUser.first_name) {
      // Only first name available
      return capitalizeName(adminUser.first_name);
    }
    if (adminUser.name) {
      // Use full name from name field
      const nameParts = adminUser.name.split(' ');
      if (nameParts.length >= 2) {
        return `${capitalizeName(nameParts[0])} ${capitalizeName(nameParts.slice(1).join(' '))}`.trim();
      }
      return capitalizeName(nameParts[0]);
    }
    if (adminUser.email) {
      return capitalizeName(adminUser.email.split('@')[0]);
    }
    // Fallback: use translated role name when no user name is available
    return t('roles.admin');
  };
  
  const adminName = getAdminDisplayName();
  
  // Get translated role
  const getTranslatedRole = () => {
    if (!adminUser.role) {
      return t('roles.administrator');
    }
    const roleLower = adminUser.role.toLowerCase();
    // Map common role values to translation keys
    if (roleLower === 'admin' || roleLower === 'administrator') {
      return t(`roles.${roleLower}`);
    }
    // For other roles, capitalize first letter and translate if key exists
    const roleKey = roleLower.charAt(0).toLowerCase() + roleLower.slice(1);
    return t(`roles.${roleKey}`, { defaultValue: adminUser.role.charAt(0).toUpperCase() + adminUser.role.slice(1) });
  };
  
  const adminRole = getTranslatedRole();

  // Load logo banner (from Banner Management) once
  useEffect(() => {
    fetch(`${API_BASE_URL}/banners?type=website&active=true`)
      .then((res) => res.json())
      .then((data) => {
        if (!data || !data.success || !Array.isArray(data.banners)) return;
        const logoBanner = data.banners.find((b) => (b.title || '').toLowerCase().includes('logo'));
        if (logoBanner && logoBanner.image_url) {
          setLogoUrl(logoBanner.image_url);
        }
      })
      .catch(() => {});
  }, []);

  // Grouped navigation sections matching user dashboard structure
  // Each item has a 'roles' array defining who can see it
  const navSections = [
    {
      title: t('sections.overview'),
      items: [
        { id: 'overview', label: userRole === 'agent' ? 'Dashboard Home' : t('overview'), icon: 'dashboard', badge: null, roles: ['admin', 'agent'] },
        { id: 'analytics-reporting', label: t('analytics'), icon: 'bar_chart', badge: null, roles: ['admin'] }
      ]
    },
    {
      title: t('sections.management'),
      items: [
        { id: 'course-management', label: t('courses'), icon: 'school', badge: null, roles: ['admin', 'agent'] },
        { id: 'resources-management', label: t('resourcesManagement'), icon: 'folder_special', badge: null, roles: ['admin', 'agent'] },
        { id: 'user-management', label: t('users'), icon: 'people', badge: null, roles: ['admin'] },
        { id: 'banner-management', label: t('bannerManagement.label'), icon: 'image', badge: null, roles: ['admin'] },
        { id: 'partnerships', label: 'Partnership Management', icon: 'handshake', badge: null, roles: ['admin'] },
        { id: 'notifications-alerts', label: t('notifications.label'), icon: 'notifications', badge: null, roles: ['admin', 'agent'] }
      ]
    },
    {
      title: t('sections.business'),
      items: [
        { id: 'pricing-billing', label: t('payments.label'), icon: 'attach_money', badge: null, roles: ['admin', 'agent'] },
        { id: 'marketing-promotions', label: t('marketing.label'), icon: 'campaign', badge: null, roles: ['admin'] },
        { id: 'customer-support', label: t('support.label'), icon: 'support_agent', badge: null, roles: ['admin'] }
      ]
    },
    {
      title: t('sections.system'),
      items: [
        { id: 'api-integration', label: tApi('label'), icon: 'code', badge: null, roles: ['admin'] },
        { id: 'privacy-security', label: t('security'), icon: 'lock', badge: null, roles: ['admin'] },
        { id: 'audit-logs', label: t('auditLogs'), icon: 'history', badge: null, roles: ['admin', 'agent'] }
      ]
    },
    {
      title: t('sections.account'),
      items: [
        { id: 'account-settings', label: t('account'), icon: 'settings', badge: null, roles: ['admin', 'agent'] }
      ]
    }
  ];

  // Filter sidebar items by user role (userRole already declared above)
  const filteredSections = navSections.map(section => ({
    ...section,
    items: section.items.filter(item => 
      (item.roles || ['admin']).includes(userRole)
    )
  })).filter(section => section.items.length > 0);

  const handleNotificationClick = () => {
    onTabChange('notifications-alerts');
  };

  // Get initials for profile placeholder
  const getInitials = (name) => {
    if (!name) return 'A';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className={`sidebar user-dashboard-style ${sidebarOpen ? 'open' : ''}`}>
      {/* Header */}
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <img src={getImageUrl(logoUrl) || logoUrl} alt="Syllera" />
          <h2>Syllera</h2>
        </div>

        {/* Profile Section */}
        <div className="profile-section">
          <div className="profile-picture">
            {profileImage ? (
              <img src={profileImage} alt="Profile" />
            ) : (
              <div className="profile-placeholder">{getInitials(adminName)}</div>
            )}
            <div className="upload-overlay" onClick={() => document.getElementById('profile-upload')?.click()}>
              <i className="material-icons">photo_camera</i>
            </div>
            <input
              id="profile-upload"
              type="file"
              accept="image/*"
              onChange={(event) => {
                const file = event.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    setProfileImage(e.target.result);
                  };
                  reader.readAsDataURL(file);
                }
              }}
              style={{ display: 'none' }}
            />
          </div>
          <div className="profile-info">
            <h3>{adminName}</h3>
            <p>{adminRole}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {filteredSections.filter(section => section.items && section.items.length > 0).map((section, sectionIndex) => (
          <div key={sectionIndex} className="nav-section">
            <div className="nav-section-title">{section.title}</div>
            <ul className="nav-list">
              {section.items.map((item) => {
                // Debug: Log audit-logs item
                if (item.id === 'audit-logs') {
                  console.log('Audit Logs tab found in sidebar:', item);
                }
                return (
                  <li key={item.id} className="nav-item">
                    <button
                      className={`nav-link ${activeTab === item.id ? 'active' : ''}`}
                      onClick={() => {
                        console.log('Tab clicked:', item.id, item.label);
                        onTabChange(item.id);
                      }}
                    >
                      <span className="nav-icon">
                        <i className="material-icons">{item.icon}</i>
                      </span>
                      <span className="nav-text">{item.label}</span>
                      {item.id === 'notifications-alerts' && (
                        <div style={{ marginLeft: 'auto' }}>
                          <NotificationBadge onClick={handleNotificationClick} />
                        </div>
                      )}
                      {item.badge && (
                        <span className="nav-badge">{item.badge}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer - Empty for now, logout moved to Top Bar user menu */}
      <div className="sidebar-footer">
        {/* Logout is now in Top Bar user menu (industry standard) */}
      </div>

      {/* Theme Toggle Button */}
      <button
        type="button"
        className="theme-toggle"
        onClick={toggleTheme}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-pressed={isDark}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        <i className="material-icons">{isDark ? 'light_mode' : 'dark_mode'}</i>
      </button>
    </div>
  );
};

export default Sidebar; 